import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  CheckSquare,
  Film,
  Image as ImageIcon,
  Images,
  LayoutGrid,
  Loader2,
  Pencil,
  Play,
  Plus,
  Power,
  PowerOff,
  Search,
  Square,
  Trash2,
  X,
} from 'lucide-react';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { WebsiteGalleryItemModal } from './WebsiteGalleryItemModal';
import {
  useBulkDeleteGalleryItems,
  useDeleteGalleryItem,
  useUpdateGalleryItem,
  useWebsiteGallery,
} from '../../services/httpServices/queries';
import type { WebsiteGalleryItem } from '../../services/httpServices/websiteService';

type TypeFilter = 'all' | 'image' | 'video';

const TYPE_TABS: { id: TypeFilter; label: string; icon: React.ElementType }[] = [
  { id: 'all', label: 'All assets', icon: LayoutGrid },
  { id: 'image', label: 'Images', icon: ImageIcon },
  { id: 'video', label: 'Videos', icon: Film },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

/**
 * Single home for every public gallery asset. Images and YouTube videos are
 * stored together and split by the type tabs, so admins manage the whole
 * gallery from one screen.
 */
export function WebsiteGallery() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [type, setType] = useState<TypeFilter>('all');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WebsiteGalleryItem | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WebsiteGalleryItem | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isFetching, error } = useWebsiteGallery({
    page,
    limit: 24,
    search: debouncedSearch,
    type: type === 'all' ? '' : type,
    status,
  });

  const items = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;

  const updateMutation = useUpdateGalleryItem();
  const deleteMutation = useDeleteGalleryItem();
  const bulkDeleteMutation = useBulkDeleteGalleryItems();

  // Debounce the search box so typing collapses into one request.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Changing type/status should jump back to the first page.
  useEffect(() => {
    setPage(1);
    setSelected([]);
  }, [type, status]);

  // Drop selections that are no longer on screen (after paging/filtering).
  useEffect(() => {
    const visible = new Set(items.map((item) => item.id));
    setSelected((prev) => {
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [items]);

  const allSelected = items.length > 0 && items.every((item) => selected.includes(item.id));
  const someSelected = items.some((item) => selected.includes(item.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelected(allSelected ? [] : items.map((item) => item.id));
  };

  const toggleOne = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (item: WebsiteGalleryItem) => {
    setEditing(item);
    setModalOpen(true);
  };

  const handleToggleActive = async (item: WebsiteGalleryItem) => {
    try {
      await updateMutation.mutateAsync({ id: item.id, data: { isActive: !item.isActive } });
    } catch {
      toast.error('Failed to update asset');
    }
  };

  const handleDeleteOne = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Asset deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete asset');
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selected];
    try {
      await bulkDeleteMutation.mutateAsync(ids);
      toast.success(`Deleted ${ids.length} asset${ids.length === 1 ? '' : 's'}`);
      setSelected([]);
      setBulkConfirmOpen(false);
    } catch {
      toast.error('Failed to delete selected assets');
    }
  };

  const hasFilters = !!debouncedSearch || type !== 'all' || !!status;
  const isInitialLoading = isLoading && items.length === 0;

  const emptyMessage = useMemo(() => {
    if (hasFilters) return 'No assets match your filters.';
    return 'No gallery assets yet. Add your first image or video to get started.';
  }, [hasFilters]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <Images className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">Gallery</h1>
            <p className="text-sm text-text-muted">
              Manage the images and YouTube videos shown on the public gallery.
            </p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add media
        </button>
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-4 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 lg:w-48"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {TYPE_TABS.map((tab) => {
            const Icon = tab.icon;
            const active = type === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setType(tab.id)}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary text-white'
                    : 'bg-background border border-border text-text-muted hover:text-text'
                }`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
          {meta && (
            <span className="ml-auto text-xs text-text-muted">
              {meta.total} asset{meta.total === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>

      {/* Selection bar */}
      {selected.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selected.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelected([])}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isInitialLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error && items.length === 0 ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load gallery assets. Please try again.</span>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-surface rounded-xl border border-dashed border-border py-16 text-center">
          <Images className="w-10 h-10 text-text-muted mx-auto mb-3" />
          <p className="text-text font-medium">{emptyMessage}</p>
          {hasFilters ? (
            <button
              onClick={() => {
                setSearch('');
                setType('all');
                setStatus('');
              }}
              className="mt-2 text-sm text-primary hover:underline"
            >
              Clear filters
            </button>
          ) : (
            <button
              onClick={openCreate}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
            >
              <Plus size={16} />
              Add media
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Select-all row */}
          <div className="flex items-center gap-2 px-1">
            <label className="inline-flex items-center gap-2 text-sm text-text-muted cursor-pointer select-none">
              <input
                ref={headerCheckboxRef}
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
              />
              Select all on this page
            </label>
            {isFetching && (
              <span className="inline-flex items-center gap-1.5 text-xs text-text-muted">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Updating…
              </span>
            )}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((item) => {
              const isSelected = selected.includes(item.id);
              return (
                <div
                  key={item.id}
                  className={`group bg-surface border rounded-xl overflow-hidden transition-all ${
                    isSelected ? 'border-primary ring-1 ring-primary/40' : 'border-border hover:border-primary/40'
                  }`}
                >
                  <div className="relative aspect-video bg-background overflow-hidden">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title || (item.type === 'video' ? 'Video thumbnail' : 'Gallery image')}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-text-muted">
                        {item.type === 'video' ? <Film size={26} /> : <ImageIcon size={26} />}
                      </div>
                    )}

                    {/* Selection toggle */}
                    <button
                      type="button"
                      onClick={() => toggleOne(item.id)}
                      aria-label={isSelected ? 'Deselect asset' : 'Select asset'}
                      className="absolute top-2 left-2 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-surface/90 border border-border text-text-muted hover:text-primary backdrop-blur-sm transition-colors"
                    >
                      {isSelected ? (
                        <CheckSquare size={15} className="text-primary" />
                      ) : (
                        <Square size={15} />
                      )}
                    </button>

                    {/* Type badge */}
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                      {item.type === 'video' ? <Film size={11} /> : <ImageIcon size={11} />}
                      {item.type === 'video' ? 'Video' : 'Image'}
                    </span>

                    {/* Video play affordance */}
                    {item.type === 'video' && (
                      <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/85 shadow-lg group-hover:scale-105 transition-transform">
                          <Play size={18} className="text-primary ml-0.5" fill="currentColor" />
                        </span>
                      </span>
                    )}

                    {/* Inactive veil */}
                    {!item.isActive && (
                      <span className="absolute inset-0 flex items-end bg-black/45">
                        <span className="m-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white">
                          Hidden
                        </span>
                      </span>
                    )}

                    {/* Hover actions */}
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(item)}
                        title={item.isActive ? 'Hide from website' : 'Show on website'}
                        className="p-1.5 rounded-lg bg-surface/90 text-text-muted hover:text-primary transition-colors"
                      >
                        {item.isActive ? <PowerOff size={15} /> : <Power size={15} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        title="Edit"
                        className="p-1.5 rounded-lg bg-surface/90 text-text-muted hover:text-primary transition-colors"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(item)}
                        title="Delete"
                        className="p-1.5 rounded-lg bg-surface/90 text-text-muted hover:text-danger transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  <div className="p-3">
                    <p className="text-sm font-medium text-text truncate">
                      {item.title || (item.type === 'video' ? 'Untitled video' : 'Untitled image')}
                    </p>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="text-xs text-text-muted">
                        Order {item.displayOrder}
                      </span>
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          item.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                        }`}
                      >
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-surface rounded-xl border border-border">
            <Pagination
              page={page}
              totalPages={meta?.totalPages || 1}
              total={meta?.total || 0}
              limit={24}
              onPageChange={setPage}
              itemName="assets"
            />
          </div>
        </div>
      )}

      <WebsiteGalleryItemModal
        isOpen={modalOpen}
        item={editing}
        onClose={() => setModalOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete ${deleteTarget?.type || 'asset'}?`}
        description="This removes the asset from the public gallery. This cannot be undone."
        confirmLabel="Delete asset"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteOne}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selected.length} asset${selected.length === 1 ? '' : 's'}?`}
        description="This removes the selected assets from the public gallery. This cannot be undone."
        confirmLabel={`Delete ${selected.length} asset${selected.length === 1 ? '' : 's'}`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
